<template>
  <fragment>
    <tr>
      <td class="productForm__itemName">
        <template>
          <div v-if="exclusiveChoice" class="radio-inline" :class="{ disabled }">
            <input
              type="radio"
              :name="`${item.slug}-${deliveryId}`"
              :id="item.slug"
              :checked="isExclusiveItemSelected"
              @click="handleExclusiveItemChange"
              :disabled="disabled"
            />
          </div>
          <label
            v-else
            class="checkbox-inline"
            :class="{ disabled }"
          >
            <input
              type="checkbox"
              :name="item.slug"
              :id="item.slug"
              :checked="isItemSelected"
              @change="handleNormalItemChange"
              :disabled="disabled"
            />
          </label>
        </template>
        <div>
          <div v-if="isBestseller" class="bestseller-badge mb-2">
            Bestseller
          </div>
          <label :for="item.slug" class="mr-2 pointer">{{ item.name }}</label>
          <span class="price-per-item">
            <fragment v-if="parseFloat(price) > 0">
              {{ parseFloat(price).toFixed(2) }} zł / sztuka
            </fragment>
            <fragment v-else>gratis!</fragment>
          </span>
          <slot name="action"></slot>
        </div>
        <a
          v-if="getModalType"
          href="#"
          class="confectionRow__link"
          @click.prevent="handleModalOpen"
        >
          <Icon v-tooltip="tooltipMessage" icon="info" />
        </a>
      </td>
      <td class="productForm__itemValue">
        <ValidationProvider
          v-if="canEnterAmount && (isItemSelected || isExclusiveItemSelected)"
          :rules="`confectionMaxBaseOnProducts:${totalProductsQuantityOnDeliveryPoint}`"
          v-slot="{ errors, classes }"
        >
          <InputSpinners @increase="handleIncreaseQuantity" @decrease="handleDecreaseQuantity" :disabled="disabled">
            <input
              type="number"
              min="0"
              name="productQuantity"
              :class="['form-control', classes]"
              :value="quantity"
              :disabled="disabled"
              onfocus="this.select();"
              @input="handleManualQuantityInput"
              @wheel="$event.target.blur()"
            >
          </InputSpinners>
          <ul v-if="errors.length > 0" class="errors text-left mt-3 mb-0">
            <li class="text-danger" v-for="(error,idx) in errors" :key="idx">{{ error }}</li>
          </ul>
        </ValidationProvider>
        <ValidationProvider v-else :name="item.slug" :rules="dependencyValidationRules" v-slot="{ errors }">
          <span class="text-danger error-message" v-for="(error, index) in errors" :key="index">{{ error }}</span>
        </ValidationProvider>
      </td>
      <td class="productForm__itemPrice" data-label="Suma">
        <span>{{ (effectiveQuantity * price).toFixed(2) }} zł</span>
      </td>
    </tr>
    <slot name="inputs"></slot>
    <confection-modal
      v-if="getModalType"
      @handleModalOpen="handleModalOpen"
      :isOpen="isConfectionModalOpen"
      :modalType="getModalType"
      :confectionItemSlug="item.slug"
      :isExclusiveConfectionItem="exclusiveChoice"
    />
  </fragment>
</template>

<script>
import _ from 'lodash';
import { mapState } from 'vuex';
import Icon from '@/components/Icon.vue';
import ConfectionModal from '@/components/Modals/ConfectionModal.vue';
import { confectionModalMixin } from '@/components/Modals/confectionModalMixin';
import { DataLayerManager } from '@/utils/DataLayerManager';
import { checkProductIsDeliveryPoint, NDE_CONFECTION } from '@/utils/categoryUtils';
import { NDEConfectionTooltips } from '@/utils/confection/tooltips';
import { filterProductsForDeliveryNumber } from '@/utils/confection/filters';
import InputSpinners from '../Inputs/InputSpinners.vue';
import { resolveConfectionModalType } from '@/utils/confection/modals';

const confectionDependencies = {
  [NDE_CONFECTION.ETUI_PUT_IN]: [
    {
      baseProduct: NDE_CONFECTION.ETUI,
      errorMessage: 'Nie można zamówić bez etui',
    },
  ],
  [NDE_CONFECTION.ETUI_PERSONALISATION]: [
    {
      baseProduct: NDE_CONFECTION.ETUI,
      errorMessage: 'Nie można zamówić bez etui',
    },
    {
      baseProduct: NDE_CONFECTION.ETUI_PUT_IN,
      errorMessage: 'Nie można zamówić bez włożenia do etui',
    },
  ],
};

export default {
  name: 'NDEConfectionRowForm',
  components: {
    ConfectionModal,
    Icon,
    InputSpinners,
  },
  mixins: [confectionModalMixin],
  props: {
    item: Object,
    deliveryId: Number,
    exclusiveChoice: Boolean,
    canEnterAmount: {
      type: Boolean,
      default: false,
    },
    isMainForm: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      quantity: 0,
      lastQuantity: 0,
      price: 0,
    };
  },
  created() {
    this.quantity = this.getInitialQuantity();
    this.lastQuantity = this.quantity;
    this.price = this.item.price || 0;
    this.setIsItemSelected(!!this.quantity);
  },
  computed: {
    ...mapState({
      selectedExclusiveItem: state => state.confection.selectedExclusiveItem,
      selectedConfectionItems: state => state.confection.selectedConfectionItems,
    }),
    isItemSelected() {
      return this.selectedConfectionItems[this.item.slug];
    },
    isExclusiveItemSelected() {
      return this.selectedExclusiveItem === this.item.slug;
    },
    allProductsInCart() {
      const productsWithoutConfection = this.$store.getters['delivery/getItemsWithoutConfectionWithoutGrouping'];
      const productsWithoutDeliveryPoints = productsWithoutConfection.filter(
        item => !checkProductIsDeliveryPoint(item),
      );
      return productsWithoutDeliveryPoints;
    },
    totalProductsQuantity() {
      const products = this.allProductsInCart;
      return _.sumBy(products, product => product.quantity);
    },
    totalProductsQuantityOnDeliveryPoint() {
      return this.getTotalProductsQuantityByDeliveryPoint(this.deliveryId);
    },
    getModalType() {
      const parentId = this.$store.getters.allProducts[0].idproducts;
      return resolveConfectionModalType(parentId, this.item.product_category);
    },
    tooltipMessage() {
      return NDEConfectionTooltips[this.item.product_category] || '';
    },
    allDeliveryIds() {
      const deliveryPointsCount = this.$store.getters['delivery/getDeliveryPointsCount'];
      const deliveryIds = [...Array(deliveryPointsCount).keys()];
      return deliveryIds;
    },
    displayedQuantity() {
      if (this.isMainForm) {
        return this.totalProductsQuantity;
      }
      return this.totalProductsQuantityOnDeliveryPoint;
    },
    effectiveQuantity() {
      return this.canEnterAmount ? this.quantity : this.displayedQuantity;
    },
    dependencyValidationRules() {
      return `confectionDependencyMet:${this.dependencyError}`;
    },
    dependencyError() {
      if (!this.isItemSelected && !this.isExclusiveItemSelected) return '';

      const productCategory = this.item.product_category;
      const dependencies = confectionDependencies[productCategory];
      if (!dependencies || !Array.isArray(dependencies)) return '';

      // eslint-disable-next-line no-restricted-syntax
      for (const dependency of dependencies) {
        const baseProductCategory = dependency.baseProduct;
        const baseProductsInCart = this.$store.getters['delivery/getItemsByCategory'](this.deliveryId, baseProductCategory);
        if (baseProductsInCart.length === 0) return dependency.errorMessage;
      }
      return '';
    },
    isBestseller() {
      return this.item.product_category === NDE_CONFECTION.ETUI;
    },
  },
  methods: {
    getInitialQuantity() {
      if (this.canEnterAmount) {
        const deliveryItem = this.$store.getters['delivery/getValue'](
          this.deliveryId,
          this.item.idproducts,
        )[0];
        return deliveryItem ? deliveryItem.quantity : 0;
      }

      if (this.isMainForm) {
        return this.$store.getters['delivery/getTotalProductQuantity'](
          this.item.idproducts,
        );
      }
      return this.getTotalProductsQuantityByDeliveryPoint(this.deliveryId);
    },
    setIsItemSelected(value) {
      if (this.disabled) return;
      const isSelected = value || false;
      this.$store.dispatch('confection/handleConfectionItemSelection', {
        selectedItem: this.item.slug,
        isSelected,
      });
    },
    getTotalProductsQuantityByDeliveryPoint(deliveryId) {
      const products = this.allProductsInCart;
      const productsForDeliveryNumber = filterProductsForDeliveryNumber(
        products,
        deliveryId,
      );
      return _.sumBy(productsForDeliveryNumber, product => product.quantity);
    },
    handleNormalItemChange(e) {
      const isSelected = e.target.checked;
      if (this.canEnterAmount && !isSelected && this.quantity > 0) {
        this.setQuantity(0, this.deliveryId);
      }
      this.setIsItemSelected(isSelected);
    },
    handleExclusiveItemChange() {
      const selectedSlug = this.isExclusiveItemSelected ? null : this.item.slug;
      this.$store.dispatch('confection/setSelectedExclusiveItem', selectedSlug);
    },
    handleManualQuantityInput(e) {
      const parsedValue = parseFloat(e.target.value);
      const quantity = Number.isNaN(parsedValue) || parsedValue < 0 ? 0 : parsedValue;
      this.setQuantity(quantity, this.deliveryId);
    },
    handleIncreaseQuantity() {
      this.setQuantity(this.quantity + 1, this.deliveryId);
    },
    handleDecreaseQuantity() {
      this.setQuantity(Math.max(this.quantity - 1, 0), this.deliveryId);
    },
    setQuantity(quantity, deliveryId) {
      this.quantity = quantity;
      this.handleQuantityChange(quantity, deliveryId);
    },
    handleQuantityChange(value, deliveryId) {
      const newProduct = {
        id: this.item.idproducts,
        quantity: parseFloat(value),
        price: parseFloat(this.price),
        name: this.item.name,
        iddelivery: parseFloat(deliveryId),
        product_category: this.item.product_category,
        analyticsData: this.item.analyticsData,
      };
      this.$store.dispatch('delivery/addProductToCart', newProduct);
      this.handleDataLayerEvent(newProduct);
    },
    handleDataLayerEvent(product) {
      if (this.lastQuantity > this.quantity) {
        const quantityDiff = this.lastQuantity - this.quantity;
        DataLayerManager.pushRemoveCartEvent(product, quantityDiff);
      } else if (this.lastQuantity < this.quantity) {
        const quantityDiff = this.quantity - this.lastQuantity;
        DataLayerManager.pushAddToCartEvent(product, quantityDiff);
      }
      this.lastQuantity = this.quantity;
    },
    toggleQuantity(isSelected) {
      const setQuantityForDelivery = (id) => {
        const quantity = isSelected
          ? this.getTotalProductsQuantityByDeliveryPoint(id)
          : 0;
        this.setQuantity(quantity, id);
      };
      if (this.isMainForm) {
        this.allDeliveryIds.forEach(setQuantityForDelivery);
      } else {
        setQuantityForDelivery(this.deliveryId);
      }
    },
    syncManualAmountQuantity() {
      if (!this.canEnterAmount) return;

      const deliveryItem = this.$store.getters['delivery/getValue'](this.deliveryId, this.item.idproducts)[0];
      const quantity = deliveryItem ? deliveryItem.quantity : 0;

      const normalizedQuantity = Number(quantity) || 0;
      this.quantity = normalizedQuantity;
      this.lastQuantity = normalizedQuantity;
      this.setIsItemSelected(normalizedQuantity > 0);
    },
  },
  watch: {
    totalProductsQuantityOnDeliveryPoint(val) {
      // when user changes main product quantity in particular delivery point form,
      // it should update quantities of all confection products in this delivery points
      // (does not apply when canEnterAmount is true)
      if (this.canEnterAmount) return;
      const isSelected = this.isItemSelected || this.isExclusiveItemSelected;
      if (!isSelected || this.isMainForm) return;
      this.setQuantity(val, this.deliveryId);
    },
    totalProductsQuantity(val) {
      // when user changes main product quantity on the first step, it should update
      // quantities of all confection products in all delivery points
      // (does not apply when canEnterAmount is true)
      if (this.canEnterAmount) return;
      const isSelected = this.isItemSelected || this.isExclusiveItemSelected;
      if (!isSelected || !this.isMainForm) return;
      const deliveryIds = this.allDeliveryIds;
      deliveryIds.forEach((id) => {
        const quantity = this.getTotalProductsQuantityByDeliveryPoint(id);
        this.setQuantity(quantity, id);
      });
    },
    isExclusiveItemSelected(val) {
      if (this.canEnterAmount) return;
      this.toggleQuantity(val);
    },
    isItemSelected(val) {
      if (this.canEnterAmount) return;
      this.toggleQuantity(val);
    },
    quantity(val) {
      this.setIsItemSelected(val > 0);
    },
    allProductsInCart: {
      handler() {
        this.syncManualAmountQuantity();
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.confectionRow__link {
  cursor: pointer;
}
</style>
